import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useMouseDownOutside } from './inputs/utils';
import { F1 } from '../../new/helpers/types';

export const useModal = <T,>(
  Elem: (props: T) => any,
  { onClose: onCloseCallback }: { onClose?: () => any } = {},
) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => setIsOpen(true);
  const close = () => {
    setIsOpen(false);
    onCloseCallback?.();
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        close();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const Popup = (props: Omit<T, 'onClose'>) =>
    isOpen ? (
      <>
        {createPortal(
          <Elem onClose={close} {...(props as any)} />,
          document.body,
        )}
      </>
    ) : null;

  return { isOpen, open, close, Popup };
};

export const useThenableModal = <T,>(Elem: (props: T) => any) => {
  const [isOpen, setIsOpen] = useState(false);

  const listener = useRef<any>(null);

  const open = (): PromiseLike<any> => {
    setIsOpen(true);
    return {
      then: ((cb) => {
        listener.current = cb;
      }) as any,
    };
  };
  const close = (data?) => {
    setIsOpen(false);
    listener.current?.(data);
    listener.current = null;
  };

  const Popup = (props: Omit<T, 'onClose'>) =>
    isOpen ? (
      <>
        {createPortal(
          <Elem onClose={close} {...(props as any)} />,
          document.body,
        )}
      </>
    ) : null;

  return { isOpen, open, close, Popup };
};

type ShadowPopupWrapper = {
  onClose: F1<boolean>;
  disableBlur?: boolean;
};
export const ShadowPopupWrapper = (
  p: PropsWithChildren<ShadowPopupWrapper>,
) => {
  const childRef = useRef<HTMLDivElement | null>(null);

  useMouseDownOutside(childRef, () => p.onClose(true));

  return (
    <div
      className={`legacy fixed z-[9999999] left-0 top-0 bottom-0 right-0 bg-black bg-opacity-70 ${
        p.disableBlur ? '' : 'backdrop-blur-[2px]'
      } overflow-auto font-primary`}
    >
      {p.children}
    </div>
  );
};

export const FormModalWrapper = ({
  title,
  children,
  onClose,
  disableBlur,
}: {
  title: ReactNode;
  children: ReactNode;
  onClose?;
  disableBlur?: boolean;
}) => {
  return (
    <ShadowPopupWrapper disableBlur={disableBlur} {...{ onClose }}>
      <div className="my-[200px] mx-auto max-w-[690px] rounded-3xl bg-white shadow-lg">
        <div className="flex items-center px-7 py-5 bg-[#EFEFF7] font-semibold tracking-wide text-2xl rounded-t-3xl">
          <div className="flex-grow">{title}</div>
          <div className="cursor-pointer" onClick={() => onClose()}>
            <FontAwesomeIcon
              icon={faTimes as any}
              className="text-[#8E9499] ml-3 leading-none"
              onClick={() => onClose()}
            />
          </div>
        </div>

        <div className="px-7 pt-5 pb-7">{children}</div>
      </div>
    </ShadowPopupWrapper>
  );
};
